import { FC } from "react";
import { useDispatch } from "react-redux";

import { useChartsContext } from "contexts";
import { updateChart, updateChartByPath } from "store/features";

import { useChartEntities } from "hooks/charts";

import { IChartScreenshotSettings } from "models/chart";
import { WidgetComponentType } from "models/screenshot";

import {
  AutomationHandler,
  useRegisterHandlerWithBroker
} from "components/automation/contexts/AutomationBrokerContext";
import { AutomationCommand } from "components/automation/types/automation";
import { SetChartPayload } from "components/automation/types/payloads";
import { ChartSettingsResult, CommandResult } from "components/automation/types/results";
import { Topic, Topics } from "components/automation/types/topics";
import { useScreenshotDispatch } from "components/screenshot/hooks";

export const ChartAutomationHandler: FC = () => {
  const dispatch = useDispatch();
  const screenshotDispatch = useScreenshotDispatch();
  const { id } = useChartsContext();
  const entities = useChartEntities(id);

  const handleChartSetting = async (command: AutomationCommand) => {
    const payload = command.payload as SetChartPayload;
    if (!payload) {
      return {
        status: "error",
        error: "payload required"
      } as CommandResult;
    }
    dispatch(
      updateChart({
        id,
        ...payload
      })
    );
    const result: CommandResult = {
      status: "ok"
    };
    return result;
  };

  const handler: AutomationHandler = {
    id: "chart-automation-handler",
    handles: function (topic: Topic): boolean {
      return (
        topic == Topics.SetChartSettings ||
        topic == Topics.GetChartSettings ||
        topic == Topics.OpenChartScreenshot
      );
    },

    executeCommand: async function (command: AutomationCommand): Promise<CommandResult> {
      if (command.topic == Topics.SetChartSettings) {
        return await handleChartSetting(command);
      }

      if (command.topic == Topics.OpenChartScreenshot) {
        const widget = {
          widgetId: id,
          widgetComponentType: WidgetComponentType.Chart
        };

        const widgetState = {
          request: entities.request,
          response: entities.apiResponse
        };
        const next = { ...entities.screenshot, visible: true };
        screenshotDispatch({
          payload: {
            settings: next?.properties?.preset as IChartScreenshotSettings,
            widget,
            widgetState
          }
        });
        dispatch(
          updateChartByPath({
            id,
            path: `features.screenshot.active`,
            value: true
          })
        );
      }

      if (command.topic == Topics.GetChartSettings) {
        return {
          status: "ok",
          payload: {
            settings: entities
          } as ChartSettingsResult
        };
      }

      const result: CommandResult = {
        status: "ok"
      };
      return result;
    }
  };
  useRegisterHandlerWithBroker(handler);

  return <></>;
};
